/**
 * Die Infoseite zum geschlossenen Playtest (Produktentscheidung vom 04.10.2026).
 *
 * Sie steht ganz vorn - vor Heldenauswahl, vor Menue, vor allem. Wer kein
 * Mitglied der Testrunde ist, soll das lesen, bevor er sich durch ein Menue
 * klickt, das ihn ohnehin nicht weiterlaesst.
 *
 * Robin hat am 04.10.2026 entschieden: Ohne Code geht gar nichts, auch kein
 * Spiel gegen den Testgegner. Begruendung dort - die Tuer soll ganz vorn
 * stehen und nicht halb offen sein.
 *
 * Zwei Wege fuehren von hier weg:
 * - Wer einen Code hat, gibt ihn ein.
 * - Wer keinen hat, geht zur Anmeldung auf der Homepage.
 *
 * Diese Seite ist KEINE Sicherung. Sie fragt den Server, was gilt, und zeigt
 * es an. Hereingelassen wird in `onAuth` auf dem Server.
 */

import { useState } from 'react';
import { t } from '../i18n/index.js';
import { anmeldeAdresse, frageZugang } from '../net/zugangstor.js';
import { ladeZugangscode, putzeZugangscode, speichereZugangscode } from '../net/zugangscode.js';
import { MenueHintergrund } from './MenueHintergrund.js';
import { MENUE_LOGO } from './menueBilder.js';

interface Props {
  /** Wird gerufen, sobald ein gueltiger Code eingegeben wurde. */
  onOffen: () => void;
}

export function Zugangstor({ onOffen }: Props) {
  /*
   * Vorbelegt mit dem gemerkten Code. Steht man wieder hier, weil der Code
   * nicht mehr gilt, muss er nur korrigiert und nicht neu getippt werden.
   */
  const [code, setCode] = useState(ladeZugangscode);
  const [pruefe, setPruefe] = useState(false);
  const [abgelehnt, setAbgelehnt] = useState(false);

  async function absenden(): Promise<void> {
    const sauber = putzeZugangscode(code);
    if (sauber === '' || pruefe) return;

    setPruefe(true);
    setAbgelehnt(false);
    const antwort = await frageZugang(sauber);
    setPruefe(false);

    /*
     * Keine Antwort heisst nicht "abgelehnt" - der Server ist dann schlicht
     * nicht erreichbar. Den Code trotzdem merken und durchlassen: Am Menue
     * scheitert der Beitritt dann mit der gewohnten Verbindungsmeldung, und
     * die ist hilfreicher als ein falsches "Code stimmt nicht".
     */
    if (antwort === null || antwort.erlaubt) {
      speichereZugangscode(sauber);
      onOffen();
      return;
    }

    setAbgelehnt(true);
  }

  /*
   * Im Stil des Menues (15.10.2026): dasselbe Kopfbild, der Schriftzug oben,
   * der Kasten mit goldenen Ecken. Fuer viele ist das hier das Erste, was sie
   * von DUELINE sehen - es soll schon aussehen wie das Spiel.
   */
  return (
    <div className="dl-gate dl-gate--bild">
      <MenueHintergrund />
      <div className="dl-menu__inhalt">
        <header className="dl-menu__kopf">
          <h1 className="dl-menu__title">
            <picture>
              <source type="image/avif" srcSet={MENUE_LOGO.avif} sizes="min(640px, 60vw)" />
              <source type="image/webp" srcSet={MENUE_LOGO.webp} sizes="min(640px, 60vw)" />
              <img className="dl-menu__logo" src={MENUE_LOGO.ersatz} alt={t.menu.title} />
            </picture>
          </h1>
        </header>

        <div className="dl-gate__panel">
          <p className="dl-gate__badge">{t.gate.badge}</p>

          <p className="dl-gate__lead">{t.gate.lead}</p>

          <form
            className="dl-menu__code"
            onSubmit={(event) => {
              event.preventDefault();
              void absenden();
            }}
          >
            <input
              className="dl-menu__input"
              value={code}
              onChange={(event) => {
                setCode(event.target.value);
                setAbgelehnt(false);
              }}
              placeholder={t.menu.access.placeholder}
              inputMode="text"
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              aria-label={t.menu.access.label}
            />
            <button
              type="submit"
              className="dl-button dl-button--primary"
              disabled={putzeZugangscode(code) === '' || pruefe}
            >
              {pruefe ? t.gate.checking : t.gate.enter}
            </button>
          </form>

          {abgelehnt && (
            <p className="dl-menu__status dl-menu__status--error">{t.menu.errors.accessInvalid}</p>
          )}

          <p className="dl-gate__or">{t.gate.or}</p>

          <a
            className="dl-button dl-gate__apply"
            href={anmeldeAdresse()}
            target="_blank"
            rel="noreferrer"
          >
            {t.gate.apply}
          </a>

          <p className="dl-menu__note">{t.menu.access.hint}</p>
        </div>
      </div>
    </div>
  );
}
